import { useFormik } from 'formik';
import { pick } from 'lodash';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useDeleteEntityDocumentMutation,
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Dropzone, RadioButton, RadioButtonProps } from 'ui';

export enum WholesaleCertificateDocType {
  Certificate = 'certificate',
}

interface WholesaleCertificate {
  wholesale_type: string;
  documents: Document[];
}

const validationSchema = yup.object<WholesaleCertificate>({
  wholesale_type: yup.string().trim().required('This field is required'),
  documents: yup.array().of(
    yup.object({
      data: yup.string().trim().required(),
      type: yup.string().trim().required(),
    }),
  ),
});

const radioButtonItems: RadioButtonProps<string>['items'] = [
  {
    label:
      'I am/we are applying for Units at a price, or for the value of at least $500,000 under this Application Form.',
    value: 'units',
  },
  {
    label:
      'I have/we have net assets of at least $2.5 million, and I am/we are applying for Units in the Fund for a purpose other than for use in connection with a business',
    value: 'assets',
  },
  {
    label:
      'I have/we have a gross income for each of the last two financial years of at least $250,000 per year, and I am/we are applying for Units in the Fund for a purpose other than for use in connection with a business',
    value: 'gross',
  },
  {
    label: 'I am/we are a ‘professional investor’ as defined in the Corporations Act',
    value: 'professional',
  },
];

interface Document {
  data: string;
  type: string;
}

export const WholesaleCertificate = ({
  moveForward,
  entity_type_prefix,
  allowSkip,
}: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation({});

  const [isFileLoading, setIsFileLoading] = useState(false);

  const getInitialValue = () => {
    return {
      documents: (data?.entity?.document_files || [])
        ?.filter(item => item.type === WholesaleCertificateDocType.Certificate)
        ?.map(item => ({
          data: item.url ?? '',
          type: item.type ?? '',
          index: item.index ?? '',
        })),
      wholesale_type: get(data?.entity, `${entity_type_prefix}.wholesale_type`),
    };
  };

  const formik = useFormik<WholesaleCertificate>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: getInitialValue(),
    onSubmit: async values => {
      if (values.documents.length < 1) {
        formik.setFieldError('documents', 'Please upload document');
        return;
      }
      const initInfo = pick<WholesaleCertificate, keyof WholesaleCertificate>(values, [
        'wholesale_type',
      ]);
      const entityTypeDetails = data?.entity[entity_type_prefix];
      const currentInfo = pick<WholesaleCertificate, keyof WholesaleCertificate>(
        entityTypeDetails,
        ['wholesale_type'],
      );

      const documents =
        values?.documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')) ?? [];
      if (!isEqual(initInfo, currentInfo) || documents?.length > 0) {
        await updateEntity({
          ...data?.entity,
          documents,
          [entity_type_prefix]: {
            ...data?.entity[entity_type_prefix],
            wholesale_type: values.wholesale_type,
          },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const onFileAccepted = async (file: File) => {
    setIsFileLoading(true);

    const base64 = await fileToBase64(file, true);

    await formik.setFieldValue('documents', [
      ...formik.values.documents,
      {
        type: WholesaleCertificateDocType.Certificate,
        data: base64,
      },
    ]);
    await formik.setFieldError('documents', '');

    setIsFileLoading(false);
  };

  const onFileClear = async () => {
    const current = formik.values.documents.find(
      doc => doc.type === WholesaleCertificateDocType.Certificate,
    );
    if (current?.data?.toLowerCase()?.startsWith('http')) {
      await deleteDocument({
        entity_id: data?.entity.id || 0,
        type: WholesaleCertificateDocType.Certificate,
      });
    }
    await formik.setFieldValue(
      'documents',
      formik.values.documents.filter(doc => doc.type !== WholesaleCertificateDocType.Certificate),
    );
    await formik.setFieldError('documents', '');
  };

  const handleChangeRadio = (value: string) => {
    formik.setFieldValue('wholesale_type', value);
    formik.setFieldError('wholesale_type', '');
  };

  return (
    <OnboardingWrapper
      title="Wholesale investor certificate"
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <div className="w-full flex flex-col items-start justify-center space-y-2">
          <p className="text-base">
            How investors qualify to be a wholesale or professional investors
          </p>
          <p className="text-gray-600 text-[14px] leading-[22px]">
            The Investor declares that they are applying for securities as a Wholesale Client or
            Sophisticated Investor as defined in the Corporations Act 2001 (Cth) and satisfy one of
            the following categories
          </p>
        </div>
        <div className="w-full flex flex-col space-y-4 mt-6 mb-8">
          <RadioButton<string>
            onChange={handleChangeRadio}
            items={radioButtonItems}
            value={formik.values.wholesale_type}
            error={formik.errors.wholesale_type}
          />
        </div>
        <div className="w-full flex flex-col space-y-2 mb-4">
          <p className="text-[14px] leading-[22px]">
            Upload document JPG, PNG or PDF. Up to 10 mb Please provide your Wholesale Investor
            Certificate as an attachment with your application form.
          </p>
          <p className="text-gray-600 text-[12px] leading-[20px]">
            The Wholesale Investor Certificate needs to be provided by your accountant. If you are
            investing $500,000.00 or more, your wholesale investor certificate will not be required.
            Further information about this regime is available here
          </p>
        </div>
        <Dropzone
          onFileAccepted={onFileAccepted}
          onFileClear={onFileClear}
          loading={isFileLoading || isDocumentDeletion}
          value={
            formik.values.documents.find(
              ({ type }) => WholesaleCertificateDocType.Certificate === type,
            )?.data
          }
          error={formik.errors.documents as string}
        />
      </>
    </OnboardingWrapper>
  );
};
